// Ski v3 withheld scores as the companion and Today readers see them (consumer inventory 5.5).
//
// A withheld ski score (Closed, or No lift access for piste and off-piste) has no number:
// - condition and observation columns store a null score with the label "Closed" or
//   "No lift access";
// - lean hourly points keep `score: 0` with `st` ("closed" or "no_lift_access"), so legacy
//   readers see a floor (lib/conditions/stored-precomputed-scores.ts);
// - a forecast day whose piste headline is withheld keeps `score: 0` with the withheld label
//   and `scoreAvailable: false` (lib/conditions/ski-live/publish.ts).
// Those zeros are placeholders, never scores, so these helpers drop them before a reader
// averages a day, picks a best window or ranks a week. Revision 2 data carries no `st` and
// never uses either label, so it passes through unchanged. Pure: no I/O, no clock.
import { SKI_LABEL_CLOSED, SKI_LABEL_NO_LIFT_ACCESS } from "@/lib/conditions/ski-labels-v3";

const SKI_HOURLY_SPORTS = new Set(["piste", "off-piste", "touring"]);

/** True for a published withheld ski label ("Closed" or "No lift access"). */
export function isWithheldSkiLabel(label: unknown): boolean {
  return label === SKI_LABEL_CLOSED || label === SKI_LABEL_NO_LIFT_ACCESS;
}

/**
 * True when a ski sport's stored label says its score is withheld: "Closed" for any ski
 * sport, and "No lift access" for piste and off-piste (touring needs no lifts).
 */
export function isWithheldSkiLabelFor(sport: string, label: unknown): boolean {
  if (!SKI_HOURLY_SPORTS.has(sport)) return false;
  return label === SKI_LABEL_CLOSED || (label === SKI_LABEL_NO_LIFT_ACCESS && sport !== "touring");
}

/**
 * True when a stored ski hour's score is withheld. Touring needs no lifts, so no lift access
 * never withholds a touring hour.
 */
export function isWithheldSkiHour(sport: string, point: unknown): boolean {
  if (!SKI_HOURLY_SPORTS.has(sport) || typeof point !== "object" || point === null) return false;
  const st = (point as { st?: unknown }).st;
  return st === "closed" || (st === "no_lift_access" && sport !== "touring");
}

/** The same hourly payload without withheld ski hours; returned as is when none are present. */
export function withoutWithheldSkiHours<T extends { sportHourlyScores: Record<string, unknown[]> }>(value: T): T {
  let changed = false;
  const next: Record<string, unknown[]> = {};
  for (const [sport, points] of Object.entries(value.sportHourlyScores)) {
    if (!Array.isArray(points)) {
      next[sport] = points;
      continue;
    }
    const kept = points.filter((point) => !isWithheldSkiHour(sport, point));
    if (kept.length !== points.length) changed = true;
    next[sport] = kept;
  }
  return changed ? { ...value, sportHourlyScores: next as T["sportHourlyScores"] } : value;
}

/**
 * True when a stored forecast day's headline (piste) score is a withheld placeholder:
 * `scoreAvailable: false` with the label "Closed" or "No lift access".
 */
export function isWithheldForecastHeadline(day: unknown): boolean {
  if (typeof day !== "object" || day === null) return false;
  const { scoreAvailable, label } = day as { scoreAvailable?: unknown; label?: unknown };
  return scoreAvailable === false && isWithheldSkiLabel(label);
}
